import React, { useState, useMemo, useRef } from 'react';
import { useUi, useAssetCdn } from '@/context/Ui';
import { findSoundsInString } from '@/utils/sounds';
import { SymbolType, CdnObjectType, SUFFIXES } from '@/types';
import { AudioPlayer, CdnMedia, RoundPlayButton } from '@/components';
import { WhitePanel } from '../components/WhitePanel';
import { ActivityTitleStep } from '../components/ActivityTitleStep';
import { Activity, ActivityStep } from '../Activity';
import { ActivityTypeProps } from '../types';

interface WordWithoutSuffixStepProps extends ActivityTypeProps {
  index: number;
  total: number;
  word: string;
}

const WordWithoutSuffixStep = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
  index,
  total,
  word,
}: WordWithoutSuffixStepProps) => {
  const playerRef = useRef<AudioPlayer>(null);

  const { sendAlert } = useUi();

  const mediaUrl = useAssetCdn(word, SymbolType.Word, CdnObjectType.Audio);

  const [playedOnce, setPlayedOnce] = useState(false);

  const [playerReady, setPlayerReady] = useState(false);

  return (
    <ActivityStep
      stepIdentifier={`${activity.id}-${index}-1`}
      activity={activity}
      mode={mode}
      stepNumber={index + 1}
      totalSteps={total}
      wrapperClassName={wrapperClassName}
      isPreview={isPreview}
      showTitle={true}
      showActivityPagination={false}
    >
      <RoundPlayButton
        color="white"
        className="my-6"
        onClick={() => {
          playerRef.current?.play();
          setPlayedOnce(true);
        }}
        ariaLabel="Play sound"
        size="sm"
        disabled={!playerReady}
        pressHint={!playedOnce}
      />

      <WhitePanel className="my-6" mode="letter" kind="bigBox">
        <CdnMedia
          className="block mx-auto w-auto h-full"
          symbol={word}
          symbolType={SymbolType.Word}
          objectType={CdnObjectType.Image}
          width={200}
          height={200}
        />
      </WhitePanel>

      <h2 className="font-sassoonInfant text-6xl text-center">{word}</h2>

      <AudioPlayer
        ref={playerRef}
        src={mediaUrl || ''}
        onReadySrc={() => setPlayerReady(true)}
        onLoadTimeoutSrc={() =>
          sendAlert('We could not load some of the audio for this lesson.', {
            type: 'warning',
            title: 'There was a problem',
          })
        }
      />
    </ActivityStep>
  );
};

interface WordWithSuffixStepProps extends ActivityTypeProps {
  index: number;
  total: number;
  word: string;
  suffix: string;
}

const WordWithSuffixStep = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
  index,
  total,
  word,
  suffix,
}: WordWithSuffixStepProps) => {
  const playerRef = useRef<AudioPlayer>(null);

  const { sendAlert } = useUi();

  const wordWithSuffix = `${word}${suffix}`;

  const mediaUrl = useAssetCdn(
    wordWithSuffix,
    SymbolType.Word,
    CdnObjectType.Audio,
  );

  const [playedOnce, setPlayedOnce] = useState(false);

  const [playerReady, setPlayerReady] = useState(false);

  return (
    <ActivityStep
      stepIdentifier={`${activity.id}-${index}-1`}
      activity={activity}
      mode={mode}
      stepNumber={index + 1}
      totalSteps={total}
      wrapperClassName={wrapperClassName}
      isPreview={isPreview}
      showTitle={true}
      showActivityPagination={false}
    >
      <RoundPlayButton
        color="white"
        className="my-6"
        onClick={() => {
          playerRef.current?.play();
          setPlayedOnce(true);
        }}
        ariaLabel="Play sound"
        size="sm"
        disabled={!playerReady}
        pressHint={!playedOnce}
      />

      <WhitePanel className="my-6" mode="letter">
        <CdnMedia
          className="block mx-auto w-auto h-full"
          symbol={wordWithSuffix}
          symbolType={SymbolType.Word}
          objectType={CdnObjectType.Image}
          width={200}
          height={200}
        />
      </WhitePanel>

      <h2 className="font-sassoonInfant text-6xl text-center">
        {wordWithSuffix}
      </h2>

      <AudioPlayer
        ref={playerRef}
        src={mediaUrl || ''}
        onReadySrc={() => setPlayerReady(true)}
        onLoadTimeoutSrc={() =>
          sendAlert('We could not load some of the audio for this lesson.', {
            type: 'warning',
            title: 'There was a problem',
          })
        }
      />
    </ActivityStep>
  );
};

export const ReadingReviewRootWordsAndSuffixes = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
}: ActivityTypeProps) => {
  if (!activity.words) {
    throw new Error('Words are required for this activity');
  }

  if (!activity.sounds) {
    throw new Error('Sounds are required for this activity');
  }

  const { words } = activity;

  const suffixes = useMemo(
    () => activity?.sounds && findSoundsInString(SUFFIXES, activity.sounds),
    [activity],
  );

  if (!suffixes) {
    throw new Error('Suffixes could not be derived for this activity');
  }

  const suffix = useMemo(() => suffixes?.[0], [suffixes]);

  const NUMBER_OF_STEPS_PER_WORD = 2;
  const TOTAL_STEPS = 1 + NUMBER_OF_STEPS_PER_WORD * words.length;

  return (
    suffix && (
      <Activity
        steps={[
          <ActivityTitleStep
            key={`${activity.id}-title`}
            activity={activity}
            mode={mode}
            wrapperClassName={wrapperClassName}
            isPreview={isPreview}
            index={0}
            total={TOTAL_STEPS}
          />,
          ...words.flatMap((word, index) => [
            <WordWithoutSuffixStep
              key={`${activity.id}-${index}-1`}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
              wrapperClassName={wrapperClassName}
              index={index + 1}
              total={TOTAL_STEPS}
              word={word}
            />,
            <WordWithSuffixStep
              key={`${activity.id}-${index}-2`}
              activity={activity}
              mode={mode}
              isPreview={isPreview}
              wrapperClassName={wrapperClassName}
              index={index + 1}
              total={TOTAL_STEPS}
              word={word}
              suffix={suffix}
            />,
          ]),
        ]}
      />
    )
  );
};
